import { ChangeDetectionStrategy, Component, computed, input, signal, WritableSignal } from '@angular/core';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { map, Observable } from 'rxjs';
import { CoreAppComponent } from 'src/app/core/core-app.component.js';
import { ItemType } from 'src/app/core/core.models.js';
import { IButton } from '../buttons/index.js';
import { ConfirmMode } from '../dialog/index.js';
import { FlexSize } from '../flex/index.js';
import { ISearchInputResult } from '../form-inputs/index.js';
import { IconColor, IconType } from '../icons/index.js';
import { SimpleTableItem } from '../simple-table/index.js';
import { TagType } from '../tag/index.js';
import { IImage } from '../ui-models.js';

interface IResolvedAvatar {
	name: string;
	image: IImage | undefined;
}

interface IResolvedItem<T extends IBaseTableItem> {
	id: string;
	avatar?: IResolvedAvatar;
	mainRouterLink?: string;
	mainIcon?: ITableIcon;
	mainItemClick?: () => void;
	title: string;
	subtitle?: string;
	subtitle2?: string;
	fields: IResolvedField[];
	buttons?: IResolvedTableItemButton[];
	mobileSimpleTableItems: SimpleTableItem[];
	rawItem: T;
}

interface IResolvedField {
	title: string;
	format: TableFieldFormat;
	value: string | number | undefined;
	tagType?: TagType;
}

interface IResolvedTableHeaderField<T extends IBaseTableItem> {
	title: string;
	sortBy?: (item: T) => string | number | Date | undefined;
}

type ResolvedTable<T extends IBaseTableItem> = {
	readonly items: readonly IResolvedItem<T>[];
	readonly fields: readonly IResolvedTableHeaderField<T>[];
	readonly config: ITable<T>;
	readonly showButtonColumn: boolean;
};

export interface ITableItemButton {
	title: string;
	icon: IconType;
	routerLink?: string;
	action?: () => void;
	confirm?: {
		text?: string;
		mode?: ConfirmMode;
	};
}

export interface IResolvedTableItemButton extends ITableItemButton {
	id: string;
}

export interface IBaseTableItem {
	id: string;
}

export type TableFieldFormat = 'text' | 'relativeTime' | 'date' | 'tag';

export interface ITableField<TItem extends IBaseTableItem> {
	title: string;
	format: TableFieldFormat;
	value: (item: TItem) => string | number | undefined;
	tagType?: (item: TItem) => TagType | undefined;
	sortBy?: (item: TItem) => string | number | Date | undefined;
	isDefaultSort?: boolean;
	defaultSortOrder?: SortOrder;
	showOnMobiles?: boolean;
}

export interface ITableSearch {
	type: 'local';
}

export interface ITableTitleColumn<TItem extends IBaseTableItem> {
	title: string;
	value: (item: TItem) => string;
}

export interface ITableAvatar {
	image?: IImage;
	name: string;
}

export interface ITableIcon {
	type: IconType;
	color: IconColor;
}

export interface ITablePagingConfig {
	pageSize: number;
}

export interface ITable<TItem extends IBaseTableItem> {
	tableButtons?: IButton[];
	itemType?: ItemType;
	search?: ITableSearch;
	items: readonly TItem[];
	noItemsText: {
		noItems: string;
		noFilteredItems: string;
	};
	paging?: ITablePagingConfig;
	title: ITableTitleColumn<TItem>;
	fields?: ITableField<TItem>[];
	mainIcon?: (item: TItem) => ITableIcon | undefined;
	avatar?: (item: TItem) => ITableAvatar;
	mainItemClick?: (item: TItem) => void;
	mainRouterLink?: (item: TItem) => string | undefined;
	id: (item: TItem) => string;
	subtitle?: (item: TItem) => string | undefined;
	subtitle2?: (item: TItem) => string | undefined;
	itemButtons?: (item: TItem) => ITableItemButton[];
	deleteItems?: (items: TItem[]) => Observable<TItem[]>;
}

type SortOrder = 'asc' | 'desc';

interface ICurrentSort<TItem extends IBaseTableItem> {
	title: string;
	sortBy: (item: TItem) => string | number | Date | undefined;
	order: SortOrder;
}

@Component({
	selector: 'lib-table',
	templateUrl: './table.component.html',
	styleUrls: ['./table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TableComponent<T extends IBaseTableItem> extends CoreAppComponent {
	public readonly table = input.required<ITable<T>>();

	public readonly resolvedTable = computed<ResolvedTable<T>>(() => {
		const resolvedTable = this.resolveTable({
			table: this.table()
		});

		return resolvedTable;
	});

	private readonly defaultPagingSetup: ITablePagingConfig = { pageSize: 20 };

	private readonly deletedItems: WritableSignal<IBaseTableItem[]> = signal([]);
	private readonly search: WritableSignal<ISearchInputResult | undefined> = signal<ISearchInputResult | undefined>(
		undefined
	);
	public readonly selectedItems = signal<IResolvedItem<T>[]>([]);
	public readonly sort = signal<ICurrentSort<T> | undefined>(undefined);
	public readonly calculatedSort = computed<ICurrentSort<T> | undefined>(() => {
		const sort = this.sort();

		if (sort) {
			return sort;
		}

		const table = this.table();

		// try to init default sort if available
		const defaultSortField = table.fields?.find((m) => m.isDefaultSort === true);

		if (defaultSortField && defaultSortField.sortBy) {
			return {
				order: defaultSortField?.defaultSortOrder ?? 'desc',
				sortBy: defaultSortField.sortBy,
				title: defaultSortField.title
			};
		}

		return undefined;
	});

	public readonly pageIndex = signal<number>(1);

	public readonly showLoadMore = computed<boolean>(() => {
		const pagedFilteredItems = this.pagedFilteredItems();
		const filteredItems = this.filteredItems();

		if (filteredItems.length > pagedFilteredItems.length) {
			return true;
		}
		return false;
	});

	public readonly loadMoreCount = computed<number>(() => {
		const pagedFilteredItems = this.pagedFilteredItems();
		const filteredItems = this.filteredItems();

		return filteredItems.length - pagedFilteredItems.length;
	});

	public readonly pageSize = computed<number>(() => {
		const pagingConfig = this.table()?.paging ?? this.defaultPagingSetup;
		return pagingConfig.pageSize;
	});

	public readonly flexSizeOfAvatarColumn = computed<FlexSize>(() => {
		return '45px';
	});

	public readonly showDeleteButton = computed<boolean>(() => {
		if (this.selectedItems().length) {
			return true;
		}
		return false;
	});

	public readonly tableButtons = computed<IButton[]>(() => {
		const buttons: IButton[] = [];

		const tableButtons = this.resolvedTable().config.tableButtons ?? [];

		for (const tableButton of tableButtons) {
			// convert all buttons to be large
			buttons.push({
				...tableButton,
				buttonSize: 'large'
			});
		}

		return buttons;
	});

	public readonly pagedFilteredItems = computed<IResolvedItem<T>[]>(() => {
		const filteredItems = this.filteredItems();
		const pageIndex = this.pageIndex();
		const pageSize = this.pageSize();

		return filteredItems.slice(0, pageSize * pageIndex);
	});

	public readonly filteredItems = computed<IResolvedItem<T>[]>(() => {
		const resolvedTable = this.resolvedTable();
		const deletedItems = this.deletedItems();
		const sort = this.calculatedSort();
		const search = this.search();

		// filter deletedItems
		let filteredItems = resolvedTable.items.filter((filteredItem) => {
			if (deletedItems.find((m) => m.id === filteredItem.id)) {
				return false;
			}
			return true;
		});

		if (sort) {
			filteredItems = filteredItems.sort((v1, v2) => {
				const a = sort.order === 'asc' ? sort.sortBy(v1.rawItem) : sort.sortBy(v2.rawItem);
				const b = sort.order === 'asc' ? sort.sortBy(v2.rawItem) : sort.sortBy(v1.rawItem);

				if (!a || !b) {
					return 0;
				}

				if (a instanceof Date && b instanceof Date) {
					return a.valueOf() - b.valueOf();
				}

				if (stringHelper.isString(a) && stringHelper.isString(b)) {
					if (a.toString().toLowerCase() < b.toString().toLowerCase()) {
						return -1;
					}
					if (a.toString().toLowerCase() > b.toString().toLowerCase()) {
						return 1;
					}
					return 0;
				}

				return +a - +b;
			});
		}

		if (search) {
			filteredItems = filteredItems.filter((m) => {
				if (m.subtitle) {
					return (
						stringHelper.containsAll(m.title, search.searchWords) ||
						stringHelper.containsAll(m.subtitle, search.searchWords)
					);
				}

				return stringHelper.containsAll(m.title, search.searchWords);
			});
		}

		return filteredItems;
	});

	constructor() {
		super();
	}

	handleLoadMore(): void {
		if (!this.showLoadMore()) {
			return;
		}

		this.pageIndex.update((value) => value + 1);
	}

	handleSortByTitle(): void {
		const table = this.table();
		if (!table) {
			return;
		}
		let orderType: SortOrder = 'asc';

		const sort = this.calculatedSort();

		if (sort?.order === 'asc') {
			orderType = 'desc';
		}

		this.sort.set({
			title: table.title.title,
			order: orderType,
			sortBy: (item) => table.title.value(item)
		});
	}

	handleSort(field: IResolvedTableHeaderField<T>): void {
		if (field.sortBy) {
			let orderType: SortOrder = 'asc';

			const sort = this.calculatedSort();

			if (sort?.order === 'asc') {
				orderType = 'desc';
			}

			this.sort.set({
				title: field.title,
				order: orderType,
				sortBy: field.sortBy
			});
		}
	}

	handleDeleteItems(): void {
		const table = this.table();
		const selectedItems = this.selectedItems();

		if (!table?.deleteItems || !selectedItems.length) {
			return;
		}

		this.deleteItems(selectedItems.map((m) => m.rawItem));

		this.selectedItems.set([]);
	}

	handleButtonClick(event: any, button: ITableItemButton): void {
		event.preventDefault();
		event.stopPropagation();
		button.action?.();
	}

	handleSelectItem(checked: boolean, item: IResolvedItem<T>): void {
		if (checked) {
			this.selectedItems.update((oldValue) => {
				return [...oldValue, item];
			});
		} else {
			this.selectedItems.update((oldValue) => {
				return oldValue.filter((m) => m.id !== item.id);
			});
		}
	}

	handleSearch(search?: ISearchInputResult): void {
		this.search.set(search);
		this.pageIndex.set(1);
	}

	private deleteItems(rawItemsToDelete: T[]): void {
		const table = this.table();
		if (!table?.deleteItems) {
			return;
		}
		super.subscribeToObservable(
			table.deleteItems(rawItemsToDelete).pipe(
				map((deletedItems) => {
					this.deletedItems.update((oldValue) => {
						return [...oldValue, ...deletedItems];
					});

					if (table.itemType) {
						super.notifyDeleted(table.itemType);
					} else {
						super.notify(`Item deleted`);
					}
				})
			)
		);
	}

	private resolveTable(data: { readonly table: ITable<T> }): ResolvedTable<T> {
		const table = data.table;

		const resolvedHeaderFields: IResolvedTableHeaderField<T>[] = (table.fields ?? []).map<
			IResolvedTableHeaderField<T>
		>((m) => ({
			sortBy: m.sortBy,
			title: m.title
		}));

		const resolvedItems: IResolvedItem<T>[] = table.items.map<IResolvedItem<T>>((tableItem) => {
			const id = table.id(tableItem);
			const buttons: ITableItemButton[] = table.itemButtons?.(tableItem) ?? [];

			const avatar = table.avatar?.(tableItem);
			let resolvedAvatar: IResolvedAvatar | undefined = undefined;

			if (avatar) {
				resolvedAvatar = {
					name: avatar.name,
					image: avatar.image
				};
			}

			const resolvedItem: IResolvedItem<T> = {
				rawItem: tableItem,
				id: id,
				avatar: resolvedAvatar,
				title: table.title.value(tableItem),
				subtitle: table.subtitle?.(tableItem),
				subtitle2: table.subtitle2?.(tableItem),
				mainRouterLink: table.mainRouterLink?.(tableItem),
				mainIcon: table.mainIcon?.(tableItem),
				mainItemClick: table.mainItemClick
					? () => {
							table.mainItemClick?.(tableItem);
					  }
					: undefined,
				buttons: buttons.map((button) => {
					const resolvedButton: IResolvedTableItemButton = {
						id: button.title + button.routerLink,
						...button
					};

					return resolvedButton;
				}),
				mobileSimpleTableItems: [],
				fields: []
			};

			for (const tableField of table.fields ?? []) {
				const fieldValue = tableField.value(tableItem);
				const tagType = tableField.tagType?.(tableItem);

				// general field
				resolvedItem.fields.push({
					format: tableField.format,
					title: tableField.title,
					value: fieldValue,
					tagType: tagType
				});

				// mobile field
				if (this.isMobileView()) {
					if (tableField.showOnMobiles && fieldValue) {
						resolvedItem.mobileSimpleTableItems.push({
							title: tableField.title,
							value: fieldValue,
							tagType: tagType,
							valueType: tagType ? 'tag' : 'text'
						});
					}
				}
			}

			return resolvedItem;
		});

		return {
			config: table,
			fields: resolvedHeaderFields,
			items: resolvedItems,
			showButtonColumn: resolvedItems.some((m) => m.buttons?.length)
		};
	}
}
